import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  MoveRight,
  Tag,
  User,
} from "lucide-react";
import { useState, useEffect } from "react";
import { images } from "../../assets/assets";
import { Link } from "react-router-dom";
import { getImageUrl } from "../../utils/getImageUrl";
import { getAllBlogs } from "../../api";

// Format date from createdAt to "Dec 8, 2024" format
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Skeleton Loading Component
const BlogSkeleton = () => (
  <div className="group flex-shrink-0 p-2 rounded-[12px] bg-gray-200 animate-pulse">
    <div className="h-[160px] bg-gray-300 rounded-[8px] mb-2" />
    <div className="h-4 bg-gray-300 rounded mb-2 w-16" />
    <div className="h-5 bg-gray-300 rounded mb-2" />
    <div className="h-4 bg-gray-300 rounded mb-4 w-full" />
    <div className="flex gap-3 mb-3">
      <div className="h-4 bg-gray-300 rounded w-24" />
      <div className="h-4 bg-gray-300 rounded w-24" />
    </div>
    <div className="h-4 bg-gray-300 rounded w-20" />
  </div>
);

const Blogs = () => {
  const [BlogData, setBlogData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAllBlogs();
        const blogsArr = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.blogs)
          ? response.blogs
          : [];
        setBlogData(blogsArr);
      } catch (err) {
        setError("Failed to load blogs. Please try again later.");
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const safeBlogs = Array.isArray(BlogData) ? BlogData : [];
  const itemsPerView = 4;
  const maxIndex = safeBlogs.length - itemsPerView;

  const prev = () => setCurrentIndex((p) => Math.max(0, p - 1));
  const next = () => setCurrentIndex((p) => Math.min(maxIndex, p + 1));

  if (error) {
    return (
      <div className="py-10 md:py-12">
        <h1 className="text-[26px] md:text-[34px] max-w-4xl font-semibold text-center mx-auto mb-6 leading-[1.2] px-4">
          Stay Informed About Your Health
        </h1>
        <div className="text-center py-12">
          <p className="text-red-500 font-poppins font-semibold text-[16px] mb-4">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#1975FF] text-white px-6 py-2 rounded-[10px] hover:bg-[#1560d4] transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-10 md:py-12">
      <h1 className="text-[26px] md:text-[34px] max-w-4xl font-semibold text-center mx-auto mb-6 leading-[1.2] px-4">
        Stay Informed About Your Health
      </h1>

      {loading ? (
        <>
          {/* Desktop skeleton */}
          <div className="hidden md:block max-w-[1200px] mx-auto px-4 overflow-hidden">
            <div className="flex gap-3">
              {[...Array(4)].map((_, idx) => (
                <BlogSkeleton key={idx} />
              ))}
            </div>
          </div>

          {/* Mobile skeleton */}
          <div className="md:hidden px-4 overflow-hidden">
            <BlogSkeleton />
          </div>
        </>
      ) : safeBlogs.length === 0 ? (
        <div className="text-center py-12 mt-6">
          <p className="font-poppins text-[16px] text-gray-600">
            No blogs available
          </p>
        </div>
      ) : (
        <>
          {/* Desktop carousel */}
          <div className="hidden md:block max-w-[1200px] mx-auto px-4 overflow-hidden">
            <div
              className="flex gap-3 transition-transform duration-400 ease-in-out"
              style={{
                transform: `translateX(calc(-${currentIndex} * (100% / 4 + 12px / 4 * 3)))`,
              }}
            >
              {safeBlogs.map((blog, idx) => (
                <Link
                  key={idx}
                  to={`/blog-detail/${blog?.slug}`}
                  className="group flex-shrink-0 p-2 rounded-[12px] shadow-[0px_0px_25px_0px_#BDBDBD33] bg-white hover:shadow-[0px_4px_30px_0px_#BDBDBD66] hover:-translate-y-1 active:translate-y-0 active:shadow-[0px_0px_25px_0px_#BDBDBD33] transition-all duration-250 ease-in-out"
                  style={{ width: "calc(25% - 9px)" }}
                >
                  <div className="overflow-hidden rounded-[8px] mb-2">
                    <img
                      src={getImageUrl(blog.imagePath)}
                      alt={`blog-${idx}`}
                      className="w-full h-[160px] object-cover group-hover:scale-105 transition-transform duration-400 ease-in-out"
                    />
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    <Tag
                      color="#D4AF37"
                      className="w-[14px] h-[14px] flex-shrink-0"
                    />
                    <p className="font-poppins font-medium text-[13px] text-[#D4AF37]">
                      {blog.category}
                    </p>
                  </div>
                  <h3 className="font-poppins font-semibold text-[14px] md:text-base leading-[1.25] mb-2">
                    {blog.heading}
                  </h3>
                  <p className="font-poppins text-[13px] text-gray-600 line-clamp-4">
                    {blog.blogSmallDesc}
                  </p>
                  <div className="flex items-center gap-3 px-1 flex-wrap mt-4">
                    <div className="flex items-center gap-1">
                      <User color="#515151" className="w-[14px] h-[14px]" />
                      <p className="text-[#515151] font-poppins font-medium text-[12px]">
                        {blog.author?.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <CalendarDays color="#515151" className="w-[14px] h-[14px]" />
                      <p className="text-[#515151] font-poppins font-medium text-[12px]">
                        {formatDate(blog.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-1 mt-3">
                    <p className="text-[12px] font-medium font-poppins text-[#0088FF] group-hover:text-[#0060cc] transition-colors">
                      Read More
                    </p>
                    <MoveRight
                      color="#0088FF"
                      className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200"
                    />
                  </div>
                </Link>
              ))}
            </div>

            {/* Arrows + dots */}
            {safeBlogs.length > itemsPerView && (
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={prev}
                  disabled={currentIndex === 0}
                  className="p-2 border-2 border-black text-black hover:bg-black hover:text-white active:scale-95 rounded-full transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex gap-2">
                  {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentIndex(i)}
                      className={`h-2 rounded-full transition-all duration-200 ${
                        i === currentIndex
                          ? "bg-[#1975FF] w-5"
                          : "bg-gray-300 w-2 hover:bg-gray-400"
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={next}
                  disabled={currentIndex >= maxIndex}
                  className="p-2 border-2 border-black text-black hover:bg-black hover:text-white active:scale-95 rounded-full transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Mobile: single scroll */}
          <div className="md:hidden px-4 overflow-hidden">
            <div
              className="flex gap-4 transition-transform duration-400 ease-in-out"
              style={{
                transform: `translateX(calc(-${currentIndex} * (100% + 16px)))`,
              }}
            >
              {safeBlogs.map((blog, idx) => (
                <Link
                  key={idx}
                  to={`/blog-detail/${blog?.slug}`}
                  className="group flex-shrink-0 w-full p-3 rounded-[12px] shadow-[0px_0px_20px_0px_#BDBDBD44] bg-white"
                >
                  <div className="overflow-hidden rounded-[8px] mb-3">
                    <img
                      src={getImageUrl(blog.imagePath)}
                      alt={`blog-${idx}`}
                      className="w-full h-[200px] object-cover"
                    />
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    <Tag color="#D4AF37" className="w-[14px] h-[14px]" />
                    <p className="font-medium text-[13px] text-[#D4AF37]">
                      {blog.category}
                    </p>
                  </div>
                  <h3 className="font-semibold text-[16px] mb-2 leading-[1.3]">
                    {blog.heading}
                  </h3>
                  <p className="text-[14px] text-gray-600 line-clamp-4">{blog.blogSmallDesc}</p>
                  <div className="flex items-center gap-3 mt-4 flex-wrap">
                    <div className="flex items-center gap-1">
                      <User color="#515151" className="w-[14px] h-[14px]" />
                      <p className="text-[#515151] text-[13px]">{blog.author?.name}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <CalendarDays color="#515151" className="w-[14px] h-[14px]" />
                      <p className="text-[#515151] text-[13px]">{formatDate(blog.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <p className="text-[13px] font-medium text-[#0088FF]">
                      Read More
                    </p>
                    <MoveRight color="#0088FF" className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>

            {/* Mobile controls */}
            <div className="flex items-center justify-between mt-5">
              <button
                onClick={prev}
                disabled={currentIndex === 0}
                className="p-2 border-2 border-black rounded-full hover:bg-black hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-500">
                {currentIndex + 1} / {BlogData.length}
              </span>
              <button
                onClick={() =>
                  setCurrentIndex((p) => Math.min(BlogData.length - 1, p + 1))
                }
                disabled={currentIndex >= BlogData.length - 1}
                className="p-2 border-2 border-black rounded-full hover:bg-black hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Blogs;
